import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Grid3x3, ExternalLink, Loader2, Truck } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const AppSwitcher = () => {
  const [loading, setLoading] = useState(false);

  const openFlotto = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        toast.error('Nicht angemeldet', {
          description: 'Bitte melden Sie sich zuerst an.'
        });
        setLoading(false);
        return;
      }
      
      // Request SSO token from CashX
      const response = await fetch('/api/auth/sso/flotto', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'SSO fehlgeschlagen');
      }
      
      const data = await response.json();
      
      // Redirect to Flotto with SSO token
      window.location.href = data.redirect_url;
      
    } catch (error) {
      console.error('SSO Error:', error);
      toast.error('Flotto-Zugriff fehlgeschlagen', {
        description: error.message || 'SSO-Token konnte nicht erstellt werden.'
      });
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2"
          data-testid="app-switcher-button"
        >
          <Grid3x3 size={16} />
          <span className="hidden md:inline">Apps</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>CashX Apps</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Current App */}
        <DropdownMenuItem disabled className="opacity-100">
          <div className="flex items-center gap-2 w-full">
            <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">C</span>
            </div>
            <div className="flex-1">
              <div className="font-medium">CashX</div>
              <div className="text-xs text-muted-foreground">POS & Verwaltung</div>
            </div>
            <div className="text-xs text-green-600 font-medium">Aktiv</div>
          </div>
        </DropdownMenuItem>
        
        {/* Flotto App */}
        <DropdownMenuItem 
          onClick={openFlotto}
          disabled={loading}
          data-testid="open-flotto-button"
        >
          <div className="flex items-center gap-2 w-full">
            <div className="w-8 h-8 rounded bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
              <Truck className="text-orange-600 dark:text-orange-400" size={16} />
            </div>
            <div className="flex-1">
              <div className="font-medium">Flotto</div>
              <div className="text-xs text-muted-foreground">Lieferungen & Fahrer</div>
            </div>
            {loading ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              <ExternalLink size={14} className="text-muted-foreground" />
            )}
          </div>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Coming Soon */}
        <DropdownMenuItem disabled className="opacity-50">
          <div className="flex items-center gap-2 w-full">
            <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <span className="text-slate-400 text-sm">+</span>
            </div>
            <div className="flex-1">
              <div className="text-sm text-muted-foreground">Weitere Apps bald verfügbar</div>
            </div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AppSwitcher;
